import { config } from '@/lib/config';
import { getAccessToken } from '@/lib/auth/session';
import { refreshSession } from './refresh';
import { AppError } from './errors';
import type { ApiErrorBody, Ok } from './types';

/**
 * The single API client (plan §4.1). No component calls fetch directly.
 * Responsibilities:
 *  - inject the Bearer access token
 *  - send the refresh cookie on auth calls (credentials:'include')
 *  - unwrap the { data, meta } envelope; throw a typed AppError on { error }
 *  - on a non-auth 401: trigger ONE single-flight refresh, then retry the request ONCE
 */

export type QueryValue = string | number | boolean | null | undefined;
export type QueryParams = Record<string, QueryValue | QueryValue[]>;

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  query?: QueryParams;
  /** JSON body. Omit for GET/DELETE. */
  body?: unknown;
  /** Pre-built FormData for multipart upload (media). Sets no Content-Type. */
  formData?: FormData;
  signal?: AbortSignal;
  /** Internal: prevents infinite refresh loops. */
  _isRetry?: boolean;
}

function buildUrl(path: string, query?: QueryParams): string {
  const url = new URL(`${config.apiBaseUrl}${path}`, window.location.origin);
  if (query) {
    for (const [key, value] of Object.entries(query)) {
      if (value == null) continue;
      if (Array.isArray(value)) {
        for (const v of value) if (v != null) url.searchParams.append(key, String(v));
      } else {
        url.searchParams.set(key, String(value));
      }
    }
  }
  return url.pathname + url.search;
}

const AUTH_PREFIX = '/admin/auth';

async function parseError(res: Response): Promise<AppError> {
  try {
    const body = (await res.json()) as ApiErrorBody;
    if (body?.error) return AppError.fromEnvelope(body, res.status);
  } catch {
    /* fall through */
  }
  return new AppError({
    code: 'internal_error',
    message: `Request failed (${res.status}).`,
    status: res.status,
  });
}

/** Raw response of a request, including parsed headers we care about (exports). */
export interface RawResult<T> {
  data: T;
  meta?: Ok<T>['meta'];
  headers: Headers;
  status: number;
}

async function execute<T>(path: string, opts: RequestOptions): Promise<RawResult<T>> {
  const method = opts.method ?? 'GET';
  const headers: Record<string, string> = {};

  const token = getAccessToken();
  if (token) headers.Authorization = `Bearer ${token}`;

  let body: BodyInit | undefined;
  if (opts.formData) {
    body = opts.formData; // browser sets multipart boundary
  } else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }

  const isAuthCall = path.startsWith(AUTH_PREFIX);

  const res = await fetch(buildUrl(path, opts.query), {
    method,
    headers,
    body,
    // Auth calls must carry the refresh cookie. Other calls don't need it but it's harmless
    // when same-origin; keep 'include' so deployments behind the admin origin work uniformly.
    credentials: 'include',
    signal: opts.signal,
  });

  // 401 on a non-auth endpoint → refresh-then-retry once (plan §4.2).
  if (res.status === 401 && !isAuthCall && !opts._isRetry) {
    await refreshSession(); // throws + hard-logout handler fires if refresh fails
    return execute<T>(path, { ...opts, _isRetry: true });
  }

  if (!res.ok) throw await parseError(res);

  if (res.status === 204) {
    return { data: null as T, headers: res.headers, status: res.status };
  }

  // Non-JSON (e.g. CSV export) — caller uses .headers / handles blob separately.
  const contentType = res.headers.get('Content-Type') ?? '';
  if (!contentType.includes('application/json')) {
    return { data: (await res.blob()) as unknown as T, headers: res.headers, status: res.status };
  }

  const json = (await res.json()) as Ok<T>;
  return { data: json.data, meta: json.meta, headers: res.headers, status: res.status };
}

export const api = {
  async get<T>(path: string, query?: QueryParams, signal?: AbortSignal): Promise<T> {
    return (await execute<T>(path, { method: 'GET', query, signal })).data;
  },
  /** GET returning the full envelope (data + meta) for list pagination. */
  async getRaw<T>(path: string, query?: QueryParams, signal?: AbortSignal): Promise<RawResult<T>> {
    return execute<T>(path, { method: 'GET', query, signal });
  },
  async post<T>(path: string, body?: unknown, query?: QueryParams): Promise<T> {
    return (await execute<T>(path, { method: 'POST', body, query })).data;
  },
  async patch<T>(path: string, body?: unknown): Promise<T> {
    return (await execute<T>(path, { method: 'PATCH', body })).data;
  },
  async put<T>(path: string, body?: unknown): Promise<T> {
    return (await execute<T>(path, { method: 'PUT', body })).data;
  },
  async del<T = null>(path: string, body?: unknown): Promise<T> {
    return (await execute<T>(path, { method: 'DELETE', body })).data;
  },
  /** Multipart upload (media). */
  async upload<T>(path: string, formData: FormData): Promise<T> {
    return (await execute<T>(path, { method: 'POST', formData })).data;
  },
  /** Raw blob fetch for CSV export (reads X-Export-Truncated). */
  async download(path: string, query?: QueryParams): Promise<RawResult<Blob>> {
    return execute<Blob>(path, { method: 'GET', query });
  },
};
